import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Bookmark, Lightbulb, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";

import { useInsightsRun } from "@/hooks/use-insights-run";
import { usePaidMediaPrompts } from "@/hooks/use-paid-media-prompts";

import { InsightsRunApiError } from "@/apis/brand-os/insights-run";

import { cn } from "@/lib/utils";

import { InsightsComposer } from "./insights-composer";
import { InsightsPromptDrawer } from "./insights-prompt-drawer";
import { InsightsResult, InsightsResultSkeleton } from "./insights-result";

import type { SavedPrompt } from "@/schema/brand-os/paid-media-prompts";

/**
 * Gated rollout: the free-form prompt composer is restricted to internal
 * dogfooders (`COMPOSER_ALLOWED_EMAILS`). Everyone else never sees the
 * textarea — only the default analysis result.
 *
 * **Default analysis:** On mount, anyone with a `user_id` runs the curated
 * `DEFAULT_PROMPT` once (including composer allow-list users — they see this
 * plus the composer below).
 *
 * Emails are normalized to lower-case + trimmed before comparison.
 *
 * NOTE: this is a UX gate, not a security boundary — the backend doesn't
 * care who sends what prompt. Flip this list (or remove the gate
 * entirely) when we're ready to expose the composer to all users.
 */
const COMPOSER_ALLOWED_EMAILS: ReadonlySet<string> = new Set([
  // "anshul@zocket.com",
  "rokesh@zocket.com",
]);

const DEFAULT_PROMPT =
  "Give me an action plan for the next 7 days. What should I do next to improve conversions — winners I should scale, plus concrete recommendations (creative, targeting, keywords, bidding, budget) tagged with the action lever and the specific entity to act on.";

/**
 * Body shape matches `POST /api/v1/ads-intelligence/insights/run` (see product curl):
 * `prompt`, `customer_id`, `google_customer_id`, `user_id`, `organization_id`, `currency`, …
 * - `customer_id` / `google_customer_id`: same hardcoded Google Ads customer id as overview until account picker.
 * - `organization_id` is the tenant/org scope when present — not the Ads customer id.
 * - `currency` matches overview snapshot / `INR` in the example.
 */
const INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED = "7948522814";
const INSIGHTS_RUN_CURRENCY = "INR";

interface InsightsSectionProps {
  userId: string | null | undefined;
  organizationId: string | null | undefined;
  userEmail: string | null | undefined;
}

/**
 * Top-level container for the Insights & Recommendations surface.
 *
 * Owns:
 *   - composer state (prompt text)
 *   - drawer open state (saved prompts popover)
 *   - the run mutation lifecycle (loading shimmer, error states, success)
 *
 * Replaces the previous "What needs attention" card. The agent picks
 * insights vs. recommendations from the prompt — the FE never branches
 * on prompt text, only on `skill_id` in the response.
 */
export function InsightsSection({ userId, organizationId, userEmail }: InsightsSectionProps) {
  const canCompose = isComposerAllowed(userEmail);

  const [prompt, setPrompt] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const insightsRun = useInsightsRun();
  // Skip the prompts-list query for non-composer users — they have no
  // drawer, so there's nothing to populate. Saves a request per page load.
  const { prompts } = usePaidMediaPrompts(canCompose ? userId : null);

  const submit = useCallback(
    (opts?: { refresh?: boolean }) => {
      const trimmed = (canCompose ? prompt : DEFAULT_PROMPT).trim();
      if (!trimmed) return;
      insightsRun.run({
        prompt: trimmed,
        user_id: userId ?? undefined,
        organization_id: organizationId ?? undefined,
        customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
        google_customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
        currency: INSIGHTS_RUN_CURRENCY,
        refresh: opts?.refresh,
      });
    },
    [canCompose, prompt, insightsRun, userId, organizationId],
  );

  // Bypass both FE and BE caches and re-run the agent. Wired to the
  // header refresh button — the user is explicitly asking for a fresh
  // result, so this should always hit the network.
  const refreshRun = useCallback(() => submit({ refresh: true }), [submit]);

  const showRefresh = insightsRun.data !== null;
  /** Rich shell (tabs + Sparkles header) renders inside `insights-result.tsx` for both skills. */
  const showRichModuleChrome =
    insightsRun.data?.skill_id === "insights" ||
    insightsRun.data?.skill_id === "recommendations";

  // Auto-fire the default prompt once per mount when we have `user_id`
  // — composer allow-list users included (they still get the textarea).
  // Call `run` directly so empty composer state doesn't block the run.
  // `run` short-circuits on cache hits (staleTime), so revisits stay cheap.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    if (!userId) return;
    autoRanRef.current = true;
    insightsRun.run({
      prompt: DEFAULT_PROMPT.trim(),
      user_id: userId ?? undefined,
      organization_id: organizationId ?? undefined,
      customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
      google_customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
      currency: INSIGHTS_RUN_CURRENCY,
    });
  }, [userId, organizationId, insightsRun.run]);

  const handlePick = useCallback(
    (p: SavedPrompt) => {
      // Populate the textarea but do NOT auto-run — the user might want
      // to tweak before firing. Auto-running would also feel surprising
      // when the user is browsing through old prompts.
      setPrompt(p.prompt);
      // Opportunistically rehydrate from cache: if this exact prompt
      // text was run earlier in the session (and is still within
      // `gcTime`), show the previous result instead of an idle state.
      // The user can still re-fire by clicking Run.
      const cached = insightsRun.getCached({
        prompt: p.prompt,
        user_id: userId ?? undefined,
        organization_id: organizationId ?? undefined,
        customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
        google_customer_id: INSIGHTS_GOOGLE_CUSTOMER_ID_HARDCODED,
        currency: INSIGHTS_RUN_CURRENCY,
      });
      if (cached) {
        insightsRun.hydrate(cached);
      } else {
        insightsRun.reset();
      }
    },
    [insightsRun, userId, organizationId],
  );

  const refreshButton = (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
      onClick={refreshRun}
      disabled={insightsRun.isPending}
      aria-label="Re-run with fresh data"
      title="Bypass cache and re-run the agent"
    >
      <RefreshCw className={cn("size-3.5", insightsRun.isPending && "animate-spin")} />
      Refresh
    </Button>
  );

  return (
    <section className="space-y-4">
      {!showRichModuleChrome && (
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Lightbulb className="size-3.5 text-violet-500" strokeWidth={2.5} />
              Insights &amp; Recommendations
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {canCompose
                ? "Ask anything about your paid media performance"
                : "Action plan for the next 7 days"}
            </span>
            {showRefresh && refreshButton}
          </div>
        </header>
      )}

      {canCompose && (
        <InsightsComposer
          prompt={prompt}
          onPromptChange={setPrompt}
          isPending={insightsRun.isPending}
          onSubmit={submit}
          savedSlot={
            <InsightsPromptDrawer
              userId={userId}
              open={drawerOpen}
              onOpenChange={setDrawerOpen}
              onPick={handlePick}
            >
              <SavedPromptsTrigger count={prompts.length} />
            </InsightsPromptDrawer>
          }
        />
      )}

      <InsightsResultArea
        isPending={insightsRun.isPending}
        error={insightsRun.error}
        data={insightsRun.data}
        onRetry={submit}
        headerActions={showRichModuleChrome && showRefresh ? refreshButton : undefined}
      />
    </section>
  );
}

function isComposerAllowed(email: string | null | undefined): boolean {
  if (!email) return false;
  return COMPOSER_ALLOWED_EMAILS.has(email.trim().toLowerCase());
}

/**
 * The "Saved" button. The surrounding `PopoverTrigger asChild` (rendered
 * by `InsightsPromptDrawer`) wires the button's click to the drawer's
 * open state — we don't need an `onClick` here.
 */
function SavedPromptsTrigger({ count }: { count: number }) {
  return (
    <button
      type="button"
      className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-background px-2 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground"
      aria-label="Open saved prompts"
    >
      <Bookmark className="size-3.5" />
      <span>Saved</span>
      {count > 0 && (
        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold text-foreground/80">
          {count}
        </span>
      )}
    </button>
  );
}

// ---------- result area ------------------------------------------------

interface ResultAreaProps {
  isPending: boolean;
  error: InsightsRunApiError | null;
  data: ReturnType<typeof useInsightsRun>["data"];
  onRetry: () => void;
  headerActions?: ReactNode;
}

function InsightsResultArea({
  isPending,
  error,
  data,
  onRetry,
  headerActions,
}: ResultAreaProps) {
  if (isPending) {
    return <InsightsResultSkeleton />;
  }
  if (error) {
    return <RunErrorState error={error} onRetry={onRetry} />;
  }
  if (!data) {
    return <RunIdleState />;
  }
  return <InsightsResult response={data} headerActions={headerActions} />;
}

function RunIdleState() {
  return (
    <div className="rounded-lg border border-dashed bg-muted/20 p-5 text-center">
      <p className="text-xs text-muted-foreground">
        Type a prompt above and run it to see insights or recommendations here.
      </p>
    </div>
  );
}

/**
 * 503 handling per the FE handoff:
 *   - "did not load an output skill" → retry + "clarify intent" hint.
 *   - generic agent failure         → retry only.
 *   - non-503 / unparseable         → retry only.
 *
 * We avoid auto-retry — runs cost real LLM tokens and the failure
 * modes here benefit from human judgement (rephrasing) rather than
 * blind re-fire.
 */
function RunErrorState({
  error,
  onRetry,
}: {
  error: InsightsRunApiError;
  onRetry: () => void;
}) {
  const isNoSkill = error.code === "NO_SKILL_LOADED";
  return (
    <div
      className={cn(
        "rounded-lg border p-4 text-sm",
        isNoSkill
          ? "border-amber-500/30 bg-amber-500/[0.05]"
          : "border-destructive/30 bg-destructive/[0.04]",
      )}
    >
      <p className="font-medium text-foreground">
        {isNoSkill ? "Couldn't pick the right output." : "The agent run failed."}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {error.detail || error.message}
      </p>
      {isNoSkill && (
        <p className="mt-2 text-xs text-muted-foreground">
          Try clarifying whether you want a <span className="font-medium">diagnosis</span>{" "}
          ("what's broken?") or an <span className="font-medium">action plan</span>{" "}
          ("what should I do?").
        </p>
      )}
      <Button size="sm" variant="outline" className="mt-3 h-7 px-2 text-xs" onClick={onRetry}>
        <RefreshCw className="size-3.5" />
        <span className="ml-1.5">Retry</span>
      </Button>
    </div>
  );
}
